import {
  faArrowLeft,
  faArrowRight,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Box,
  Group,
  Skeleton,
  Stepper,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useSessionStorage } from "@mantine/hooks";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { type ComboboxOption, ErrorAlert } from "@sweco-se/reality-frontend";
import {
  ActionIconLink,
  DATASET_REGISTERUNIT,
  Loading,
  SelectProperties,
  TimeAgo,
} from "@sweco-se/reality-frontend";
import { PrimaryButton, SecondaryButton } from "@sweco/theme-mantine";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { type FC, Fragment, useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQuery as useGraphQLQuery } from "urql";
import { z } from "zod";

import { api } from "@/api.ts";
import { DatasetGroupIcons } from "@/components/DatasetGroupIcons.tsx";
import { graphql } from "@/gql";

import { BuyExtractOptionsStep } from "./components/BuyExtractOptionsStep.tsx";

const propertySearchSchema = z.object({
  projectNumber: z.number().optional(),
  uuids: z.string().optional(),
});

export const Route = createFileRoute("/property/search/")({
  component: PropertySearch,
  validateSearch: propertySearchSchema,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Property Search`,
      },
    ],
  }),
});

const propertiesQuery = graphql(/* GraphQL */ `
  query PropertySimple($ids: [String!]!) {
    registerUnits(ids: $ids) {
      id
      designation {
        text
      }
    }
  }
`);

const SessionsTable: FC = () => {
  const { user } = useAuth();
  const { data, isPending } = useQuery(
    useMemo(
      () =>
        api
          .from("session")
          .select(
            "id,top_level_objects,datasets,started_at,started_by,project!inner(identifier,name),product(name)",
          )
          .eq("started_by", user?.profile.email ?? user?.profile.sub ?? "")
          .gte("started_at", dayjs().subtract(10, "days").toISOString())
          .order("started_at", { ascending: false })
          .limit(10),
      [user],
    ),
  );

  const propertyIds = useMemo(
    () =>
      data
        ?.flatMap((s) => s.top_level_objects?.[DATASET_REGISTERUNIT] ?? [])
        .filter((v, i, l) => l.indexOf(v) === i),
    [data],
  );

  const [
    { data: properties, fetching: propertiesFetching, error: propertiesError },
  ] = useGraphQLQuery({
    query: propertiesQuery,
    pause: !propertyIds || propertyIds.length === 0,
    variables: { ids: propertyIds! },
  });

  const navigate = Route.useNavigate();

  if (data) {
    return (
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Trans>Project</Trans>
            </Table.Th>
            <Table.Th>
              <Trans>Searched</Trans>
            </Table.Th>
            <Table.Th>
              <Trans>Properties</Trans>
            </Table.Th>
            <Table.Th>
              <Trans>Datasets</Trans>
            </Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((row) => (
            <Table.Tr
              key={row.id}
              onClick={() =>
                navigate({ to: "./$session", params: { session: row.id } })
              }
              style={{ cursor: "pointer" }}
            >
              <Table.Td>
                {row.project.identifier} - {row.project.name}
              </Table.Td>
              <Table.Td>
                <TimeAgo date={row.started_at} />
              </Table.Td>
              <Table.Td>
                {propertiesFetching ? (
                  <Skeleton />
                ) : propertiesError ? (
                  <Text c="alert">
                    <Trans>Could not load properties</Trans>
                  </Text>
                ) : (
                  row.top_level_objects?.[DATASET_REGISTERUNIT].map(
                    (id, idx) => {
                      const designation = properties?.registerUnits.find(
                        (p) => p.id === id,
                      )?.designation;
                      return (
                        <Fragment key={id}>
                          {idx > 0 ? ", " : ""}
                          {designation ? (
                            designation.text
                          ) : (
                            <em>
                              <Trans>Unknown property</Trans>
                            </em>
                          )}
                        </Fragment>
                      );
                    },
                  )
                )}
              </Table.Td>
              <Table.Td>
                <DatasetGroupIcons include={row.product.map((p) => p.name)} />
              </Table.Td>
              <Table.Td>
                <ActionIconLink
                  from={Route.fullPath}
                  to={"./$session"}
                  params={{ session: row.id }}
                  variant="default"
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </ActionIconLink>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  } else if (isPending) {
    return <Loading />;
  } else {
    return null;
  }
};

function PropertySearch() {
  const { user } = useAuth();
  const email = user?.profile.email ?? user?.profile.sub;
  // check if the current user's email domain has a corresponding project (means they are not a Sweco user)
  const { data: customerProject, isFetching: customerProjectFetching } =
    useQuery(
      api
        .from("project")
        .select("id")
        .eq("customer_domain", email?.split("@")[1] ?? "")
        .maybeSingle(),
      {
        enabled: !!email,
      },
    );

  const [properties, setProperties] = useSessionStorage<Array<ComboboxOption>>({
    key: "selected-properties",
    defaultValue: [],
  });
  const [project, setProject] = useState<number>();
  const [invoiceMarker, setInvoiceMarker] = useState<string | null>(null);
  const [products, setProducts] = useState<Array<number>>([]);
  const [legal, setLegal] = useState(false);

  const [activeStep, setActiveStep] = useState(0);

  const { uuids, projectNumber } = Route.useSearch();

  // Query properties from URL params
  const [{ data: propertiesFromUrl, fetching: propertiesFromUrlFetching }] =
    useGraphQLQuery({
      query: propertiesQuery,
      pause: !uuids || uuids.length === 0,
      variables: { ids: uuids?.split(",") ?? [] },
    });

  const { data: projectDataFromURL, isFetching: projectDataFromURLisFetching } =
    useQuery(
      api
        .from("project")
        .select("id")
        .limit(1)
        .or(`identifier.ilike.%${projectNumber}%,name.ilike.%${projectNumber}%`)
        .maybeSingle(),
    );

  useEffect(() => {
    if (
      uuids &&
      uuids.length > 0 &&
      propertiesFromUrl?.registerUnits &&
      !propertiesFromUrlFetching &&
      (projectNumber
        ? projectDataFromURL && !projectDataFromURLisFetching
        : true)
    ) {
      if (customerProject) {
        navigateToResult(
          propertiesFromUrl.registerUnits.map((unit) => unit.id),
        );
      }

      const comboboxOptions: Array<ComboboxOption> =
        propertiesFromUrl.registerUnits.map((unit) => ({
          key: unit.id,
          value: unit.designation.text,
        }));
      setProperties(comboboxOptions);
      setActiveStep(1);

      // Set project number if provided
      if (projectDataFromURL?.id) {
        setProject(projectDataFromURL.id);
      }
    }
  }, [
    uuids,
    propertiesFromUrl,
    propertiesFromUrlFetching,
    projectDataFromURL,
    projectDataFromURLisFetching,
    customerProject,
    projectNumber,
  ]);

  const nextStep = () => {
    if (activeStep === 1) {
      navigateToResult();
    } else {
      setActiveStep((current) => (current < 2 ? current + 1 : current));
    }
  };
  const prevStep = () =>
    setActiveStep((current) => (current > 0 ? current - 1 : current));
  const canGoNext =
    activeStep === 0
      ? properties.length > 0
      : activeStep === 1
        ? properties.length > 0 &&
          (customerProject || project !== undefined) &&
          products.length > 0 &&
          legal
        : false;

  const navigate = useNavigate();
  const navigateToResult = async (ids: Array<string> = []) => {
    if (ids.length === 0) ids = properties.map((p) => p.key);
    const projectId = project ?? customerProject?.id;
    if (projectId === undefined) {
      throw new Error("Could not determine project ID");
    }
    const { data: session } = await api
      .rpc("create_session_with_products", {
        p_project_id: projectId,
        p_product_ids: products,
        p_top_level_objects: {
          [DATASET_REGISTERUNIT]: ids,
        },
        p_invoice_marker: invoiceMarker,
      })
      .throwOnError();
    await navigate({
      to: "/property/search/$session",
      params: { session: session.id },
    });
  };

  if (
    customerProjectFetching ||
    propertiesFromUrlFetching ||
    projectDataFromURLisFetching
  ) {
    return <Loading />;
  }

  if (!email?.endsWith("@sweco.se") && !customerProject) {
    return (
      <ErrorAlert>
        <Trans>You do not have access to this functionality</Trans>
      </ErrorAlert>
    );
  }

  return (
    <>
      <Title size="h2" order={1} mt="lg">
        <Trans>Property Search</Trans>
      </Title>
      <Text variant="bodyDefault" c="dimmed" mb="md">
        <Trans>Data extracted from Lantmäteriet</Trans>
      </Text>
      <Stepper
        active={activeStep}
        onStepClick={setActiveStep}
        completedIcon={<FontAwesomeIcon icon={faCheck} />}
      >
        <Stepper.Step
          label={<Trans>Select properties</Trans>}
          description={<Trans>Search, map or load from file</Trans>}
        >
          <Box maw="500px">
            <SelectProperties
              onSelected={setProperties}
              selected={properties}
            />
          </Box>
        </Stepper.Step>
        <Stepper.Step
          label={<Trans>Specify data extract</Trans>}
          description={<Trans>Dataset, responsibility and cost</Trans>}
        >
          <BuyExtractOptionsStep
            products={products}
            onProductsChange={setProducts}
            project={project ?? null}
            onProjectChange={setProject}
            properties={properties}
            invoiceMarker={invoiceMarker}
            onInvoiceMarkerChange={setInvoiceMarker}
            hasFixedProject={!!customerProject}
            onLegalChanged={setLegal}
          />
        </Stepper.Step>
      </Stepper>
      <Group mt="xl">
        {activeStep === 1 && (
          <SecondaryButton
            leftSection={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={prevStep}
          >
            <Trans>Back</Trans>
          </SecondaryButton>
        )}
        <PrimaryButton
          rightSection={
            activeStep === 0 ? (
              <FontAwesomeIcon icon={faArrowRight} />
            ) : undefined
          }
          onClick={nextStep}
          disabled={!canGoNext}
        >
          {activeStep === 0 ? (
            <Trans>Next step</Trans>
          ) : (
            <Trans>Buy data</Trans>
          )}
        </PrimaryButton>
      </Group>

      {activeStep === 0 ? (
        <>
          <Title order={4} mt="xl">
            <Trans>Previous searches</Trans>
          </Title>
          <SessionsTable />
        </>
      ) : null}
    </>
  );
}
